<template>
  <div
    class="fullscreen-wrapper"
    :class="{ 'is-exporting-pdf': isExportingPdf }"
  >
    <metrics-pdf-overlay v-if="isExportingPdf" />
    <!-- Fixed Header -->
    <div class="fullscreen-header">
      <div class="d-flex align-items-center">
        <eg-button
          type="ordinary"
          size="lg"
          icon="arrow-left"
          icon-size="large"
          icon-color="var(--color-text-neutral-on-white)"
          class="eg-back"
          @click="handleBack"
        />
        <h2 class="header-title heading-m">{{ headerTitleText }}</h2>
      </div>

      <tour-compass-button
        v-if="mode === 'create' || mode === 'edit'"
        context="sidebar_rubricBuilder"
        trigger-event="onboarding:opened:rubricBuilder"
        class="ml-auto"
      />

      <!-- Preview page actions -->
      <div v-if="mode === 'preview'" class="header-actions">
        <eg-button
          type="secondary"
          size="medium"
          icon="pen-edit"
          :disabled="isBelongsToUser"
          @click="$emit('edit')"
        >
          {{ $t("rubrics.editBtn") }}
        </eg-button>
        <eg-button
          type="secondary"
          size="medium"
          icon="share-arrow-sqaure"
          :disabled="isBelongsToUser"
          @click="$emit('share')"
        >
          {{ $t("rubrics.share") }}
        </eg-button>
        <eg-button
          type="primary"
          size="medium"
          icon="download"
          :disabled="isDownloadDisabled"
          :is-loading="isExportingPdf"
          @click="downloadRubric"
        >
          {{ $t("common.export") }}
        </eg-button>
      </div>
      <div v-if="mode === 'share'" class="header-actions">
        <eg-button type="secondary" size="medium" @click="$emit('saveAndEdit')">
          {{ $t("rubrics.saveRubric") }}
        </eg-button>
        <eg-button
          type="primary"
          size="medium"
          @click="$emit('gradeImmediately')"
        >
          {{ $t("rubrics.startGrading") }}
        </eg-button>
      </div>
    </div>
    <div class="fullscreen-content">
      <!-- Tabs Navigation -->
      <div v-if="showTabs" class="fullscreen-tabs">
        <eg-tabs v-model="activeTab" class="rubrics-tabs" no-fade>
          <eg-b-tab
            :title="mode === 'create' ? $t('common.create') : $t('common.edit')"
            active
          >
            <div class="rubric-tab-content">
              <slot></slot>
            </div>
          </eg-b-tab>
          <eg-b-tab :title="$t('rubrics.preview')" lazy>
            <div class="rubric-tab-content">
              <slot name="preview">
                <div class="preview-placeholder">
                  <p class="text-muted">{{ $t("rubrics.previewHere") }}</p>
                </div>
              </slot>
            </div>
          </eg-b-tab>
          <eg-b-tab :title="$t('rubrics.settings')" lazy>
            <div class="rubric-tab-content">
              <slot name="settings">
                <div class="settings-placeholder">
                  <p class="text-muted">
                    {{ $t("rubrics.settingsNotConfigured") }}
                  </p>
                </div>
              </slot>
            </div>
          </eg-b-tab>
        </eg-tabs>
      </div>
      <!-- Content when tabs are not shown -->
      <div
        v-show="!showTabs"
        class="rubric-tab-content"
        :class="{ 'rubric-tab-content--preview': mode === 'preview' }"
      >
        <slot></slot>
      </div>
    </div>

    <!-- Fixed Footer -->
    <div v-if="!hideFooter" class="fullscreen-footer">
      <div class="footer-actions">
        <eg-button
          v-if="mode !== 'preview'"
          type="secondary"
          size="medium"
          @click="$emit('cancel')"
        >
          {{ $t("common.cancel") }}
        </eg-button>
        <eg-button
          v-if="mode === 'edit'"
          type="secondary"
          size="medium"
          @click="$emit('delete')"
        >
          {{ $t("common.delete") }}
        </eg-button>
        <eg-button
          v-if="mode !== 'preview'"
          type="primary"
          size="medium"
          @click="handleSaveClick"
        >
          {{ saveButtonText }}
        </eg-button>
      </div>
    </div>
  </div>
</template>

<script>
import MetricsPdfOverlay from "@/views/metrics/components/shared/metrics_pdf_overlay.vue";
import TourCompassButton from "@/components/global/tour_compass_button.vue";
import { exportElementToPdf } from "@/utils/exportToPdf.js";

export default {
  name: "RubricFullscreenModal",
  components: { MetricsPdfOverlay, TourCompassButton },
  props: {
    mode: {
      type: String,
      default: "create",
      validator: (value) =>
        ["create", "edit", "preview", "share"].includes(value),
    },
    rubricName: {
      type: String,
      default: "",
    },
    headerTitle: {
      type: String,
      default: "",
    },
    hideFooter: {
      type: Boolean,
      default: false,
    },
    showTabs: {
      type: Boolean,
      default: false,
    },
    defaultTab: {
      type: [String, Number],
      default: "edit",
      validator: (value) => {
        if (typeof value === "string") {
          return ["edit", "preview", "settings"].includes(value);
        }
        return [0, 1, 2].includes(value);
      },
    },
    isSystem: {
      type: Number,
      default: 0,
    },
    rubricAuthor: {
      type: Object,
      default: null,
    },
  },
  emits: ["cancel", "save", "delete", "edit", "back", "share"],
  data() {
    return {
      activeTab: this.getTabIndex(this.defaultTab),
      isExportingPdf: false,
    };
  },
  computed: {
    headerTitleText() {
      if (
        this.mode === "preview" ||
        this.mode === "edit" ||
        this.mode === "share"
      ) {
        return this.rubricName || this.$t("rubrics.rubricFallback");
      }
      return this.headerTitle || this.$t("rubrics.rubricBuilder");
    },
    saveButtonText() {
      if (this.mode === "edit") {
        return this.$t("rubrics.saveRubric");
      }
      return this.$t("rubrics.createSaveRubric");
    },
    belongsToCurrentUser() {
      const currentUserId = this.$store.state.user?.id;
      const authorId = this.rubricAuthor?.id;

      // If no author or no current user, doesn't belong to current user
      if (!authorId || !currentUserId) {
        return false;
      }

      return currentUserId === authorId;
    },
    isBelongsToUser() {
      return !this.belongsToCurrentUser;
    },
    isDownloadDisabled() {
      return this.isSystem || !this.belongsToCurrentUser;
    },
  },
  methods: {
    getTabIndex(tab) {
      if (typeof tab === "number") {
        return tab;
      }
      const tabMap = {
        edit: 0,
        preview: 1,
        settings: 2,
      };
      return tabMap[tab] || 0;
    },
    async handleBack() {
      if (this.mode === "create") {
        const leave = await this.$showConfirmationModal({
          title: this.$t("rubrics.exitRubricTitle"),
          text: this.$t("rubrics.exitRubricText"),
          okTitle: this.$t("rubrics.continueEditing"),
          cancelTitle: this.$t("rubrics.exit"),
          warningText: this.$t("rubrics.unsavedChanges"),
        });
        if (leave) {
          this.$emit("back");
        }
      } else if (/preview|share/.test(this.mode)) {
        this.$emit("cancel");
      } else {
        this.$emit("back");
      }
    },
    handleSaveClick() {
      this.$emit("save");
    },
    async downloadRubric() {
      const now = new Date();
      const date = now.toLocaleDateString("en-CA");
      const hh = String(now.getHours()).padStart(2, "0");
      const mm = String(now.getMinutes()).padStart(2, "0");
      const filename = `${this.rubricName || "rubric"} ${date} ${hh}:${mm}`;

      this.isExportingPdf = true;
      await this.$nextTick();

      const scrollContainer =
        this.$el.querySelector(".rubric-tab-content--preview") ||
        this.$el.querySelector(".rubric-tab-content") ||
        this.$el;

      try {
        await exportElementToPdf(this.$el, filename, {
          orientation: "landscape",
          scrollContainer,
        });
      } finally {
        this.isExportingPdf = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
// Fullscreen wrapper layout
.fullscreen-wrapper {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  // dvh adjusts to actual visible viewport in Safari (accounts for browser chrome)
  height: 100dvh;
  background: var(--color-surface-neutral-background, #f8f9fa);
  z-index: 1056;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

// Fixed Header
.fullscreen-header {
  height: 68px;
  min-height: 68px;
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0 108px;
  background: var(--color-surface-neutral-default, #ffffff);
  ::v-deep {
    .align-items-center:first-of-type {
      gap: 0.25rem;
    }
  }
  @media (max-width: 1200px) {
    padding: 0 58px;
  }
}

// Tabs Navigation
.fullscreen-tabs {
  background: var(--color-surface-neutral-default, #ffffff);
  .rubrics-tabs {
    background-color: white;

    &::v-deep {
      .eg-tabs__container {
        overflow: visible !important;
      }
    }
  }
}

// Scrollable Content
.fullscreen-content {
  flex: 1;
  min-height: 0;
  overflow: hidden;

  .rubrics-tabs {
    height: 100%;

    &::v-deep {
      .tab-content {
        padding: 0;
        height: calc(100vh - 194px);
        height: calc(100dvh - 194px);
        background: var(--color-surface-neutral-canvas, #f6f8fa);
      }

      .tab-pane {
        height: 100%;
      }
      .nav-tabs {
        padding-left: 108px;
        background-color: white;
        @media (max-width: 1200px) {
          padding-left: 58px;
        }
      }
    }
  }
}

.rubric-tab-content {
  height: calc(100vh - 174px);
  height: calc(100dvh - 174px);
  padding: 1.5rem 94px 1.5rem 108px;
  scrollbar-gutter: stable;
  overflow-y: auto;
  background: var(--color-surface-neutral-canvas, #f6f8fa);
  &--preview {
    height: calc(100vh - 68px);
    height: calc(100dvh - 68px);
  }
  @media (max-width: 1200px) {
    padding: 1.5rem 44px 1.5rem 58px;
  }
}

// Fixed Footer
.fullscreen-footer {
  height: 68px;
  min-height: 68px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 0 108px;
  background: var(--color-surface-neutral-default, #ffffff);
  border-top: 1px solid var(--color-stroke-neutral-default, #e5ebf0);
}

// Header content
.header-title {
  font-size: 1.5rem;
  margin: 0;
  color: var(--color-text-neutral-on-white, #1a1a1a);
  flex: 1;
  @media (max-width: 1200px) {
    font-size: 1.2rem;
  }
}

.header-actions {
  display: flex;
  gap: 0.75rem;
  margin-left: auto;
}

// Footer content
.footer-actions {
  display: flex;
  gap: 0.75rem;
}

// Placeholder styles
.preview-placeholder,
.settings-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 300px;
}

.is-exporting-pdf {
  height: auto !important;
  overflow: visible !important;

  .fullscreen-content {
    overflow: visible !important;
    flex: unset !important;
  }

  .rubric-tab-content,
  .rubric-tab-content--preview {
    height: auto !important;
    overflow: visible !important;
  }

  ::v-deep .pdf-overlay {
    position: fixed;
  }

  ::v-deep {
    .header-actions,
    .footer-actions,
    .fullscreen-footer,
    .toggle-menu-button,
    .tour-compass-button,
    .eg-back {
      display: none !important;
    }
  }
}
</style>
